import React from 'react';
import { Box } from '@mui/material';
import { InterventionLadderCostModel } from '../../../api-service/clients';
import { formatNumber } from '../../../utilities';
import { externalCostColor, internalCostColor } from './intervention-ladder-colors';

const million = 1_000_000;

export function formatMillions(value: number) {
  if (value === 0) {
    return '0';
  }
  // A small cost must not read as nothing.
  return value < million / 20 ? '< 0,1' : formatNumber(value / million, 1);
}

export function formatActivitiesWithoutPrice(count: number) {
  return `${formatNumber(count)} ${count === 1 ? 'aktivitet' : 'aktiviteter'} uden pris`;
}

export function InterventionCostSplit({ cost }: { cost: InterventionLadderCostModel }) {
  return (
    <InternalExternalSplit
      internal={cost.internal}
      external={cost.external}
      format={formatMillions}
      label={`Intern ${formatMillions(cost.internal)} mio. kr., ekstern ${formatMillions(cost.external)} mio. kr.`}
    />
  );
}

interface InternalExternalSplitProps {
  internal: number;
  external: number;
  format: (value: number) => string;
  label: string;
}

export function InternalExternalSplit({ internal, external, format, label }: InternalExternalSplitProps) {
  const total = internal + external;
  function shareOf(value: number) {
    return total > 0 ? (value / total) * 100 : 0;
  }

  return (
    <Box>
      <Box role="img" aria-label={label} sx={{ display: 'flex', height: 10, mt: 1, borderRadius: '2px', overflow: 'hidden', bgcolor: 'rgba(24, 76, 97, 0.08)' }}>
        <Box sx={{ width: `${shareOf(internal)}%`, bgcolor: internalCostColor }} />
        <Box sx={{ width: `${shareOf(external)}%`, bgcolor: externalCostColor }} />
      </Box>
      <Box aria-hidden sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75, fontSize: '0.6875rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
        <span>{format(internal)}</span>
        <span>{format(external)}</span>
      </Box>
    </Box>
  );
}

export function InterventionCostLegend() {
  return (
    <Box sx={{ display: 'flex', gap: 1.25, fontSize: '0.6875rem', color: 'text.secondary' }}>
      <LegendItem color={internalCostColor} label="Intern" />
      <LegendItem color={externalCostColor} label="Ekstern" />
    </Box>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box component="span" sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: color }} />
      {label}
    </Box>
  );
}
